/** Company details used on the statement of account (§4.2.6). */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { Alert, Button, Card, Field, Skeleton, inputClass } from '../../components/ui';

interface CompanyProfile {
  registeredName: string;
  addressLine: string | null;
  city: string | null;
  province: string | null;
  zip: string | null;
  tin: string | null;
  contactNumber: string | null;
  email: string | null;
  bankDetails: string | null;
  soaFooterNote: string | null;
}

const EMPTY: CompanyProfile = {
  registeredName: '',
  addressLine: '',
  city: '',
  province: '',
  zip: '',
  tin: '',
  contactNumber: '',
  email: '',
  bankDetails: '',
  soaFooterNote: '',
};

export function CompanyProfilePage(): JSX.Element {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<CompanyProfile>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const profile = useQuery({
    queryKey: ['company-profile'],
    queryFn: () => api<{ data: CompanyProfile | null }>('/settings/company'),
  });

  useEffect(() => {
    if (profile.data?.data) setForm({ ...EMPTY, ...profile.data.data });
  }, [profile.data]);

  const save = useMutation({
    mutationFn: () =>
      api('/settings/company', {
        method: 'PUT',
        body: {
          registeredName: form.registeredName.trim(),
          addressLine: form.addressLine || undefined,
          city: form.city || undefined,
          province: form.province || undefined,
          zip: form.zip || undefined,
          tin: form.tin || undefined,
          contactNumber: form.contactNumber || undefined,
          email: form.email || undefined,
          bankDetails: form.bankDetails || undefined,
          soaFooterNote: form.soaFooterNote || undefined,
        },
      }),
    onSuccess: () => {
      setError(null);
      setNotice('Saved. New statements of account use these details.');
      void queryClient.invalidateQueries({ queryKey: ['company-profile'] });
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not save the profile.'),
  });

  if (profile.isLoading) return <Skeleton className="h-96 w-full" />;

  const set = (key: keyof CompanyProfile) => (value: string) => setForm({ ...form, [key]: value });

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <header>
        <h1 className="text-xl font-bold text-navy dark:text-white">Company profile</h1>
        <p className="text-sm text-slate-500">
          These details are printed at the top of every statement of account.
        </p>
      </header>

      {notice && <Alert tone="ok">{notice}</Alert>}
      {error && <Alert tone="critical">{error}</Alert>}
      {form.tin === '[TO SET]' && (
        <Alert tone="warning">
          The TIN is still a placeholder. Set the registered TIN before sending any statement.
        </Alert>
      )}

      <Card>
        <form
          className="grid gap-4 sm:grid-cols-2"
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate();
          }}
        >
          <div className="sm:col-span-2">
            <Field label="Registered name" required>
              <input
                className={inputClass}
                required
                value={form.registeredName}
                onChange={(event) => set('registeredName')(event.target.value)}
              />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Address">
              <input
                className={inputClass}
                value={form.addressLine ?? ''}
                onChange={(event) => set('addressLine')(event.target.value)}
              />
            </Field>
          </div>
          <Field label="City / municipality">
            <input
              className={inputClass}
              value={form.city ?? ''}
              onChange={(event) => set('city')(event.target.value)}
            />
          </Field>
          <Field label="Province">
            <input
              className={inputClass}
              value={form.province ?? ''}
              onChange={(event) => set('province')(event.target.value)}
            />
          </Field>
          <Field label="ZIP">
            <input
              className={inputClass}
              value={form.zip ?? ''}
              onChange={(event) => set('zip')(event.target.value)}
            />
          </Field>
          <Field label="TIN" hint="Format 000-000-000-00000.">
            <input
              className={inputClass}
              value={form.tin ?? ''}
              onChange={(event) => set('tin')(event.target.value)}
            />
          </Field>
          <Field label="Contact number">
            <input
              className={inputClass}
              value={form.contactNumber ?? ''}
              onChange={(event) => set('contactNumber')(event.target.value)}
            />
          </Field>
          <Field label="Email">
            <input
              className={inputClass}
              type="email"
              value={form.email ?? ''}
              onChange={(event) => set('email')(event.target.value)}
            />
          </Field>
          <div className="sm:col-span-2">
            <Field
              label="Bank details"
              hint="Printed as payment instructions: bank, branch, account name and number."
            >
              <textarea
                className={`${inputClass} h-24`}
                value={form.bankDetails ?? ''}
                onChange={(event) => set('bankDetails')(event.target.value)}
              />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Statement footer note">
              <textarea
                className={`${inputClass} h-20`}
                value={form.soaFooterNote ?? ''}
                onChange={(event) => set('soaFooterNote')(event.target.value)}
              />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Button type="submit" busy={save.isPending}>
              <Save aria-hidden="true" className="h-4 w-4" /> Save profile
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
